"use client";

import type { PublicationsUpdatesComponent } from "../../types";
import { PublicationCard } from "../../components/home/cards/PublicationCard";
import { StatUpdateCard } from "../../components/home/cards/StatUpdateCard";
import { ArrowRight } from "lucide-react";
import { RuntimeImage, RuntimeLink, useLocale } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";
import { getImageObjectPositionStyle, getImageOverrideProps, mergeClassNames, resolveImageValue } from "../../utils/imageOptions";

/** Publications and statistical updates section renderer with background image. */
export function PublicationsUpdatesRenderer({ component }: { component: PublicationsUpdatesComponent }) {
  const locale = useLocale();
  const { backgroundImageSrc, publications, statUpdates } = component.content;
  const { src, options } = resolveImageValue(backgroundImageSrc);
  const backgroundSrc = src || "/media/welcome_background.jpg";
  const objectPositionStyle = getImageObjectPositionStyle(options);
  const overrideProps = getImageOverrideProps(options);
  return (
    <section className={mergeClassNames("relative z-0 isolate", overrideProps.className)} style={overrideProps.style}>
      <div className="absolute inset-0" aria-hidden="true">
        <div className="relative h-full w-full">
          <RuntimeImage
            src={backgroundSrc}
            alt=""
            className="absolute inset-0 h-full w-full object-cover object-center"
            style={objectPositionStyle}
            priority
          />
        </div>
        <div className="absolute inset-0" style={{ background: 'lab(100 0 0 / 0.64)' }} aria-hidden="true" />
      </div>
      <div className="relative pb-10">
        <section className="relative py-12 pb-12  md:pb-4">
          <div className="relative mx-auto max-w-7xl px-4 md:px-8">
            <h3 className="text-[26px] font-bold text-[#005CA9] md:text-[28px]">Dernières publications</h3>
            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
              {publications.map((p) => (
                <PublicationCard key={p.id} tag={p.tag} date={p.date} title={p.title} href={p.href} />
              ))}
            </div>
            <RuntimeLink className="mt-4 inline-flex items-center gap-2 text-[18px] xl:text-[20px] text-[#0069B4]" href="#">
              {t(locale, "allPublications")} <ArrowRight className="h-4 w-4 text-[#FFA401]" />
            </RuntimeLink>
          </div>
        </section>
        <section className="relative py-0 md:py-12">
          <div className="relative mx-auto max-w-7xl px-4 md:px-8">
            <h3 className="text-[26px] font-bold text-[#005CA9] md:text-[28px]">Dernières mises à jour statistiques</h3>
            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
              {statUpdates.map((c, idx) => (
                <StatUpdateCard key={`${c.category}-${idx}`} category={c.category} period={c.period} items={c.items} />
              ))}
            </div>
            <RuntimeLink className="mt-4 inline-flex items-center gap-2 text-[18px] xl:text-[20px] text-[#0069B4]" href="#">
              {t(locale, "allStatUpdates")} <ArrowRight className="h-4 w-4 text-[#FFA401]" />
            </RuntimeLink>
          </div>
        </section>
      </div>
    </section>
  );
}


